/**
 * Google (Gemini) LLM Provider - v13 Section 6.10-6.11
 *
 * Provider for Google's Gemini models.
 *
 * Key features:
 * - 1M token context window
 * - Multimodal support (text, images, video)
 * - Competitive pricing
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import { BaseLLMProvider, LLMProviderType } from './base';
import { calculateCost } from './registry';
/**
 * Google (Gemini) LLM Provider
 */
export class GoogleProvider extends BaseLLMProvider {
    constructor(config) {
        super(config);
        if (!config.apiKey) {
            throw new Error('Google API key is required');
        }
        this.apiKey = config.apiKey;
        // Model should be explicitly configured via config.model or request.model
        // Empty default will cause error if no model specified - this is intentional
        this.defaultModel = config.model ?? '';
        // Token limit should come from ConfigService.getModelMetadata() at runtime
        this.defaultMaxTokens = config.maxTokens ?? 8192;
        this.defaultTemperature = config.temperature ?? 0.7;
    }
    getProviderType() {
        return LLMProviderType.GOOGLE;
    }
    /**
     * Get supported models
     * @deprecated Use configService.getModelsByProvider('google') for dynamic model list
     */
    getSupportedModels() {
        // No hardcoded model list - use ConfigService for actual list
        return [];
    }
    async isAvailable() {
        try {
            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${this.apiKey}`);
            return response.ok;
        }
        catch (error) {
            this.logger.debug(`Google availability check failed: ${error}`);
            return false;
        }
    }
    async complete(request) {
        const model = request.model ?? this.defaultModel;
        // Trust caller's maxTokens if provided, otherwise use default
        const maxTokens = request.maxTokens ?? this.defaultMaxTokens;
        const temperature = request.temperature ?? this.defaultTemperature;
        // Validate request
        const errors = this.validateRequest(request);
        if (errors.length > 0) {
            return this.createErrorResponse(errors.join('; '), model);
        }
        // Convert to Gemini format
        const { contents, systemInstruction } = this.formatMessagesForGemini(request.messages);
        this.logger.info(`Using model: ${model} for Google request`);
        const startTime = Date.now();
        try {
            const requestBody = {
                contents,
                generationConfig: {
                    maxOutputTokens: maxTokens,
                    temperature,
                },
            };
            if (systemInstruction) {
                requestBody.systemInstruction = { parts: [{ text: systemInstruction }] };
            }
            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${this.apiKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(`Google API error: ${response.status} ${error}`);
            }
            const data = await response.json();
            const processingTime = (Date.now() - startTime) / 1000;
            // Extract content from Gemini response
            const candidate = data.candidates?.[0];
            const content = candidate?.content?.parts?.map((p) => p.text).join('\n') ?? '';
            // Gemini uses different token field names
            const usage = data.usageMetadata ?? {};
            const inputTokens = usage.promptTokenCount ?? 0;
            const outputTokens = usage.candidatesTokenCount ?? 0;
            const totalTokens = usage.totalTokenCount ?? inputTokens + outputTokens;
            const costUsd = calculateCost(model, inputTokens, outputTokens);
            this.logger.debug('Received response from Google', {
                model,
                content_length: content.length,
                processing_time: processingTime,
                tokens_used: totalTokens,
                cost: costUsd,
            });
            return {
                content,
                model,
                usage: {
                    inputTokens,
                    outputTokens,
                    totalTokens,
                    costUsd,
                },
                finishReason: candidate?.finishReason === 'STOP' ? 'stop'
                    : candidate?.finishReason === 'MAX_TOKENS' ? 'length'
                        : (candidate?.finishReason === 'SAFETY' || candidate?.finishReason === 'RECITATION') ? 'content_filter'
                            : 'stop',
            };
        }
        catch (error) {
            const processingTime = (Date.now() - startTime) / 1000;
            const errorMsg = `Google API error: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error('Google generation failed', { model, error: errorMsg, processingTime });
            return this.createErrorResponse(errorMsg, model);
        }
    }
    /**
     * Convert messages to Gemini format
     */
    formatMessagesForGemini(messages) {
        let systemInstruction = null;
        const contents = [];
        for (const message of messages) {
            if (message.role === 'system') {
                // Combine system messages
                systemInstruction = systemInstruction
                    ? `${systemInstruction}\n\n${message.content}`
                    : message.content;
            }
            else {
                // Map user/assistant to Gemini's user/model
                contents.push({
                    role: message.role === 'assistant' ? 'model' : 'user',
                    parts: [{ text: message.content }],
                });
            }
        }
        return { contents, systemInstruction };
    }
}
